import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { ProductTagsSegment } from "@redotech/redo-conversion-core-common/post-purchase/post-purchase-flow";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { OperatorSelector } from "../../operator-selector";

interface ProductTagsSegmentCommonProps {
  condition: ProductTagsSegment;
}

export type ProductTagsSegmentProps = ProductTagsSegmentCommonProps &
  (
    | {
        readonly: false;
        setCondition: (condition: ProductTagsSegment) => void;
        searchProductTags: (params: { search: string }) => Promise<string[]>;
      }
    | { readonly: true }
  );

const cartOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "At least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

const tagListOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "any tag",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "none of the tags",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "all tags",
};

export const ProductTagsSegmentCondition = memo(
  function ProductTagsSegmentCondition({
    condition,
    ...props
  }: ProductTagsSegmentProps) {
    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, cartOperator });
        }
      },
    );

    const handleTagListOperatorSelected = useHandler(
      (tagListOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, tagListOperator });
        }
      },
    );

    return (
      <>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCartOperatorSelected}
          operatorToText={cartOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.cartOperator}
        />
        <Text fontSize="xs" textColor="primary">
          product in the cart has
        </Text>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleTagListOperatorSelected}
          operatorToText={tagListOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.tagListOperator}
        />
        <Text fontSize="xs" textColor="primary">
          in the list:
        </Text>
        {props.readonly ? (
          <Flex dir="row" gap="md" w="full" wrap="wrap">
            {condition.tagNames.length === 0 ? (
              <Text fontSize="xs" textColor="secondary">
                No tags selected
              </Text>
            ) : (
              condition.tagNames.map((tag, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={tag} />
              ))
            )}
          </Flex>
        ) : (
          <ProductTagsInput
            onTagsSelected={(tagNames) =>
              props.setCondition({ ...condition, tagNames })
            }
            searchProductTags={props.searchProductTags}
            selectedTags={condition.tagNames}
          />
        )}
      </>
    );
  },
);

const ProductTagsInput = memo(function ProductTagsInput({
  selectedTags,
  onTagsSelected,
  searchProductTags,
}: {
  selectedTags: string[];
  onTagsSelected: (tags: string[]) => void;
  searchProductTags: (params: { search: string }) => Promise<string[]>;
}) {
  const [searchString, setSearchString] = useState("");
  const debouncedSearchString = useDebounce(searchString, 500);

  const tagsLoad = useLoad(async () => {
    try {
      return await searchProductTags({ search: debouncedSearchString });
    } catch (error) {
      console.error("Error searching product tags:", error);
      return [];
    }
  }, [debouncedSearchString, searchProductTags]);

  const tagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const allTags = new Set([...(tagsLoad.value ?? []), ...selectedTags]);
    return Array.from(allTags).map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [tagsLoad.value, selectedTags]);

  const selectedTagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedTags.map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [selectedTags]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onTagsSelected(items.map((item) => item.value));
    },
  );

  return (
    <Flex w="full">
      <RedoTagsDropdownInput<string>
        description={
          selectedTags.length > 0
            ? `${selectedTags.length} tag${selectedTags.length !== 1 ? "s" : ""} selected`
            : undefined
        }
        loading={tagsLoad.pending}
        options={tagOptions}
        placeholder="Enter or select tags"
        searchPlaceholder="Search for product tags..."
        searchString={searchString}
        selectedOptions={selectedTagOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleSelectionChange}
        size="xs"
      />
    </Flex>
  );
});
